/**
 * Single source for the public site's navigation, footer, and primary CTA.
 * SiteHeader, MobileNav, and SiteFooter all read from here so a new page is
 * added once.
 */
export const CONTACT_URL = "https://calendar.app.google/4E1JwtdJMcbrdG1D8";
export const SUPPORT_EMAIL = "support@kautious.com";

export type ProductTone = "markets" | "data" | "orchestrate";

export interface NavLink {
  label: string;
  href: string;
  description?: string;
  external?: boolean;
}

export interface ProductGroup {
  name: string;
  tagline: string;
  href: string;
  tone: ProductTone;
  items: NavLink[];
}

export const PRODUCT_GROUPS: readonly ProductGroup[] = [
  {
    name: "Kautious",
    tagline: "The AI research desk for financial professionals.",
    href: "/kautious",
    tone: "markets",
    items: [
      {
        label: "Overview",
        href: "/kautious",
        description:
          "Build sheets, monitors, reports, and apps from plain English.",
      },
      {
        label: "Desktop app",
        href: "/desktop",
        description: "Native macOS research, source-grounded and read-only.",
      },
      {
        label: "Console",
        href: "/console",
        description: "Your workspace, tools, and dashboards.",
      },
    ],
  },
  {
    name: "Daytta",
    tagline: "The data platform behind Kautious.",
    href: "/daytta",
    tone: "data",
    items: [
      {
        label: "Overview",
        href: "/daytta",
        description: "Extract, Graph, and Time engines on the Llamox runtime.",
      },
      {
        label: "Kautious Extract",
        href: "/extract",
        description: "Messy documents into defensible, source-grounded data.",
      },
      {
        label: "Kautious Graph",
        href: "/graph",
        description: "Temporal memory and knowledge for AI agents.",
      },
      {
        label: "Kautious Time",
        href: "/time",
        description: "Backtested time-series forecasting you can defend.",
      },
    ],
  },
  {
    name: "Llamox",
    tagline: "The governed AI operations backend.",
    href: "/llamox",
    tone: "orchestrate",
    items: [
      {
        label: "Overview",
        href: "/llamox",
        description: "Agents build, humans approve, the platform deploys.",
      },
    ],
  },
];

export const RESOURCE_LINKS: readonly NavLink[] = [
  {
    label: "Docs",
    href: "/docs",
    description: "API reference, guides, and integration notes.",
  },
  {
    label: "Blog",
    href: "/blog",
    description: "Research notes and product updates.",
  },
  {
    label: "Investors",
    href: "/investors",
    description: "Why we are building Kautious, and how to get involved.",
  },
];

export interface FooterColumn {
  title: string;
  links: NavLink[];
}

export const FOOTER_COLUMNS: readonly FooterColumn[] = [
  {
    title: "Products",
    links: [
      { label: "Kautious", href: "/kautious" },
      { label: "Desktop app", href: "/desktop" },
      { label: "Daytta", href: "/daytta" },
      { label: "Llamox", href: "/llamox" },
      { label: "Console", href: "/console" },
    ],
  },
  {
    title: "Daytta engines",
    links: [
      { label: "Kautious Extract", href: "/extract" },
      { label: "Kautious Graph", href: "/graph" },
      { label: "Kautious Time", href: "/time" },
    ],
  },
  {
    title: "Company",
    links: [
      { label: "Docs", href: "/docs" },
      { label: "Blog", href: "/blog" },
      { label: "Investors", href: "/investors" },
      { label: "Contact", href: CONTACT_URL, external: true },
    ],
  },
];

export const LEGAL_LINKS: readonly NavLink[] = [
  { label: "Terms", href: "/terms" },
  { label: "Privacy", href: "/privacy" },
];

export const DISCLAIMER =
  "Kautious provides analytics and workflow tooling. It is not investment advice.";

export const PRODUCT_TONE_CLASS: Record<ProductTone, string> = {
  markets: "product-markets",
  data: "product-data",
  orchestrate: "product-orchestrate",
};
